// Staff "Facebook ad account cards" panel on the Invoices page: every card a
// live client submitted that still has to go on their ad account, and every
// request they have not answered yet. Each row is the same reveal card the
// onboarding workflow page uses. The submitted-card email and the reminders
// link here with ?fb-cards=<client slug>.
import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Box, Button, Card, CardContent, Chip, Stack, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import SendIcon from '@mui/icons-material/Send';

import ClientDropdown from '@src/components/ClientDropdown';
import { toast } from '@src/components/core/toaster';
import { fetchAdAccountCardQueue, requestAdAccountCard } from '@src/api/cardVault';
import AdAccountCardStaffCard from '@src/components/cardVault/AdAccountCardStaffCard';

const fmtDateTime = (iso) => (iso
  ? new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })
  : '');

export default function FacebookCardsStaffPanel() {
  const [searchParams] = useSearchParams();
  const linkedSlug = searchParams.get('fb-cards') || '';
  const [rows, setRows] = useState([]);
  const [picked, setPicked] = useState(null); // { id, name } chosen to request a card
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    fetchAdAccountCardQueue()
      .then((res) => setRows(res?.data?.rows || []))
      .catch(() => setRows([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  // The fail-safe when a client never acted on the payment-issue email: one
  // click emails their contacts the secure card link (also works as a resend).
  const sendRequest = async () => {
    if (!picked) return;
    setSending(true);
    try {
      const res = await requestAdAccountCard(String(picked.id));
      const to = res?.data?.sent_to || [];
      toast.success(`Card request emailed${to.length ? ` to ${to.join(', ')}` : ''}`);
      setPicked(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not send the card request.');
    } finally {
      setSending(false);
    }
  };

  const waiting = rows.filter((r) => r.state === 'card_waiting').length;
  const linkedInQueue = rows.some((r) => r.client_slug === linkedSlug);

  return (
    <Card id="facebook-cards" sx={{ mb: 3 }}>
      <CardContent>
        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={2} sx={{ mb: rows.length || linkedSlug ? 2 : 0 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <LockIcon fontSize="small" color="action" />
            <Typography variant="subtitle1" fontWeight={700}>Facebook ad account cards</Typography>
            {waiting > 0 && <Chip size="small" color="warning" label={`${waiting} to add`} />}
            {rows.length === 0 && (
              <Typography variant="body2" color="text.secondary">Nothing waiting.</Typography>
            )}
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" sx={{ width: { xs: '100%', sm: 'auto' } }}>
            <Box sx={{ width: { xs: '100%', sm: 300 } }}>
              <ClientDropdown
                label="Client"
                value={picked?.id ?? ''}
                onChange={(value, option) => setPicked(value ? { id: value, name: option?.label || '' } : null)}
              />
            </Box>
            <Button
              variant="contained"
              startIcon={<SendIcon />}
              onClick={sendRequest}
              disabled={!picked || sending}
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              {sending ? 'Sending…' : 'Send card request'}
            </Button>
          </Stack>
        </Stack>

        {linkedSlug && !linkedInQueue && (
          <AdAccountCardStaffCard key={`linked-${linkedSlug}`} clientSlug={linkedSlug} />
        )}
        {rows.map((r) => (
          <Box key={r.client_slug}>
            <Typography variant="body2" sx={{ fontWeight: 700, mb: 0.5 }}>
              {r.client_name}
              <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>
                {r.state === 'card_waiting'
                  ? `card submitted ${fmtDateTime(r.since)}`
                  : `asked ${fmtDateTime(r.since)}${r.request_source === 'fb_payment_email' ? ' (Facebook payment email)' : ''}, no card yet`}
              </Typography>
            </Typography>
            <AdAccountCardStaffCard clientSlug={r.client_slug} clientName={r.client_name} />
          </Box>
        ))}
      </CardContent>
    </Card>
  );
}
